import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../contexts/AuthContext";
import {
  ticketsAPI,
  usersAPI,
  categoriesAPI,
  manualTicketAPI,
} from "../services/api";
import {
  Eye,
  Search,
  Filter,
  Calendar,
  User as UserIcon,
  Tag,
  Archive,
  MessageSquarePlus,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { formatWhatsAppNumber, formatDuration } from "@/utils/formatters";

const HISTORY_FILTER_DEFAULTS = {
  searchTerm: "",
  selectedAttendant: "all",
  selectedCategory: "all",
  selectedStatus: "all",
  startDate: "",
  endDate: "",
  sortBy: "created_at",
  sortDirection: "desc",
};

const Tickets = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const canMonitorSupport = ["analyst", "admin"].includes(user?.role);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);

  const [searchTerm, setSearchTerm] = useState(HISTORY_FILTER_DEFAULTS.searchTerm);
  const [selectedAttendant, setSelectedAttendant] = useState(HISTORY_FILTER_DEFAULTS.selectedAttendant);
  const [selectedCategory, setSelectedCategory] = useState(HISTORY_FILTER_DEFAULTS.selectedCategory);
  const [selectedStatus, setSelectedStatus] = useState(HISTORY_FILTER_DEFAULTS.selectedStatus);
  const [startDate, setStartDate] = useState(HISTORY_FILTER_DEFAULTS.startDate);
  const [endDate, setEndDate] = useState(HISTORY_FILTER_DEFAULTS.endDate);
  const [sortBy, setSortBy] = useState(HISTORY_FILTER_DEFAULTS.sortBy);
  const [sortDirection, setSortDirection] = useState(HISTORY_FILTER_DEFAULTS.sortDirection);
  const [filtersReady, setFiltersReady] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);

  const tableScrollRef = useRef(null);
  const hydratedFiltersUserRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const historyFiltersStorageKey = user?.id
    ? `up2-suporte:history-filters:${user.id}`
    : null;

  useEffect(() => {
    if (!user?.id) {
      setFiltersReady(false);
      return;
    }

    if (hydratedFiltersUserRef.current === user.id) {
      setFiltersReady(true);
      return;
    }

    let savedFilters = {};
    try {
      savedFilters = JSON.parse(localStorage.getItem(historyFiltersStorageKey) || "{}");
    } catch (error) {
      console.warn("Não foi possível recuperar os filtros do histórico", error);
    }

    const restoredFilters = { ...HISTORY_FILTER_DEFAULTS, ...savedFilters };
    setSearchTerm(restoredFilters.searchTerm);
    setSelectedAttendant(restoredFilters.selectedAttendant);
    setSelectedCategory(restoredFilters.selectedCategory);
    setSelectedStatus(restoredFilters.selectedStatus);
    setStartDate(restoredFilters.startDate);
    setEndDate(restoredFilters.endDate);
    setSortBy(restoredFilters.sortBy);
    setSortDirection(restoredFilters.sortDirection);
    setCurrentPage(1);
    hydratedFiltersUserRef.current = user.id;
    setFiltersReady(true);
  }, [historyFiltersStorageKey, user?.id]);

  useEffect(() => {
    if (!filtersReady || !historyFiltersStorageKey) return;

    try {
      localStorage.setItem(
        historyFiltersStorageKey,
        JSON.stringify({
          searchTerm,
          selectedAttendant,
          selectedCategory,
          selectedStatus,
          startDate,
          endDate,
          sortBy,
          sortDirection,
        }),
      );
    } catch (error) {
      console.warn("Não foi possível salvar os filtros do histórico", error);
    }
  }, [
    filtersReady,
    historyFiltersStorageKey,
    searchTerm,
    selectedAttendant,
    selectedCategory,
    selectedStatus,
    startDate,
    endDate,
    sortBy,
    sortDirection,
  ]);

  const checkForScrollPosition = () => {
    if (tableScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current;
      
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft) < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkForScrollPosition();
    window.addEventListener("resize", checkForScrollPosition);
    return () => window.removeEventListener("resize", checkForScrollPosition);
  }, [tickets]);

  const scrollToRight = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const scrollToLeft = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const handleCreateNewTicket = async (ticketRef) => {
    try {
      toast({ title: "Abrindo atendimento..." });

      const payload = {
        client_phone: ticketRef.client_phone,
        client_name: ticketRef.client_name,
        external_client_id: ticketRef.external_client_id,
        external_client_data: ticketRef.external_client_data,
        last_wa_jid: ticketRef.last_wa_jid,
        description: "Atendimento reaberto via Histórico.",
      };

      const response = await manualTicketAPI.create(payload);

      toast({
        title: "Sucesso!",
        description: "Novo atendimento criado. Redirecionando...",
      });

      navigate(`/chat/${response.data.id}`);
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        "Não foi possível abrir um novo atendimento.";

      toast({
        title: "Atenção",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (canMonitorSupport) loadUsers();
    loadCategories();
  }, [user, canMonitorSupport]);

  useEffect(() => {
    if (!filtersReady) return;

    const delayDebounceFn = setTimeout(() => {
      setCurrentPage(1);
      loadTickets(1);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [
    searchTerm,
    selectedAttendant,
    selectedCategory,
    selectedStatus,
    startDate,
    endDate,
    sortBy,
    sortDirection,
    filtersReady,
    user,
  ]);

  useEffect(() => {
    if (filtersReady && currentPage !== 1) {
      loadTickets(currentPage);
    }
  }, [currentPage, filtersReady]);

  const loadTickets = async (page = currentPage) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        limit: 50,
        status: selectedStatus !== "all" ? selectedStatus : null,
        assigned_to: selectedAttendant !== "all" ? selectedAttendant : null,
        category: selectedCategory !== "all" ? selectedCategory : null,
        search: searchTerm || null,
        start_date: startDate || null,
        end_date: endDate || null,
        sort_by: sortBy,
        sort_direction: sortDirection,
      };

      const response = await ticketsAPI.getAll(params);
      const { items, total_pages, total } = response.data;

      setTickets(items);
      setTotalPages(total_pages);
      setTotalTickets(total);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar atendimentos.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {}
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {}
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedAttendant("all");
    setSelectedCategory("all");
    setSelectedStatus("resolved");
    setStartDate("");
    setEndDate("");
    setSortBy("created_at");
    setSortDirection("desc");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        label: "Pendente",
        className: "bg-yellow-100 text-yellow-800 border-yellow-200",
      },
      in_progress: {
        label: "Em Andamento",
        className: "bg-blue-100 text-blue-800 border-blue-200",
      },
      resolved: {
        label: "Resolvido",
        className: "bg-green-100 text-green-800 border-green-200",
      },
      closed: {
        label: "Fechado",
        className: "bg-gray-100 text-gray-800 border-gray-200",
      },
    };

    const config = statusConfig[status];
    if (!config) return <Badge className="bg-gray-100 text-gray-600">-</Badge>;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 md:gap-0 justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex gap-2">
            <Archive className="w-8 h-8 text-gray-800 shrink-0" />
            Histórico de Atendimentos
          </h1>
          <p className="text-gray-600 mt-1">
            {canMonitorSupport
              ? "Visualize o histórico de atendimentos completo"
              : "Visualize seu histórico de atendimentos completo"}
          </p>
        </div>
        <div className="text-center md:text-right">
          <p className="text-xl text-gray-600">Total de atendimentos</p>
          <p className="text-2xl font-bold text-blue-600">{tickets.length}</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="font-semibold text-gray-900">Filtros Avançados</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              <Search className="w-4 h-4 inline mr-1" />
              Buscar (Protocolo, Nome, Empresa ou Telefone)
            </label>
            <Input
              placeholder="Ex: TK-2026, WSTech, 1199999..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-white"
            />
          </div>

          {canMonitorSupport && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                <UserIcon className="w-4 h-4 inline mr-1" />
                Atendente
              </label>
              <Select
                value={selectedAttendant}
                onValueChange={setSelectedAttendant}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os atendentes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              <Tag className="w-4 h-4 inline mr-1" />
              Categoria
            </label>
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todas as categorias" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.name}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Status
            </label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendentes</SelectItem>
                <SelectItem value="in_progress">Em Andamento</SelectItem>
                <SelectItem value="resolved">Resolvidos</SelectItem>
                <SelectItem value="closed">Fechados</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              <Calendar className="w-4 h-4 inline mr-1" />
              Data Inicial
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              <Calendar className="w-4 h-4 inline mr-1" />
              Data Final
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Ordenar por
            </label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="history-sort-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Data de criação</SelectItem>
                <SelectItem value="duration">Tempo de atendimento</SelectItem>
                <SelectItem value="rating">Nota</SelectItem>
                <SelectItem value="priority">Prioridade</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Ordem
            </label>
            <Select value={sortDirection} onValueChange={setSortDirection}>
              <SelectTrigger data-testid="history-sort-direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Maior para menor</SelectItem>
                <SelectItem value="asc">Menor para maior</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button variant="outline" onClick={clearFilters}>
            Limpar Filtros
          </Button>
        </div>
      </div>

      <div className="relative bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className={`hidden lg:flex absolute left-4 top-[4.5rem] bottom-0 w-16 z-10 items-start justify-start pointer-events-none transition-opacity duration-300 ${canScrollLeft ? 'opacity-100' : 'opacity-0'}`}>
          <Button
            variant="outline"
            size="icon"
            onClick={scrollToLeft}
            className="rounded-full w-8 h-8 bg-white shadow-md border-gray-200 text-blue-600 pointer-events-auto opacity-90 hover:opacity-100"
            title="Deslizar para a esquerda"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
        </div>

        <div className={`hidden lg:flex absolute right-4 top-[4.5rem] bottom-0 w-16 z-10 items-start justify-end pointer-events-none transition-opacity duration-300 ${canScrollRight ? 'opacity-100' : 'opacity-0'}`}>
          <Button
            variant="outline"
            size="icon"
            onClick={scrollToRight}
            className="rounded-full w-8 h-8 bg-white shadow-md border-gray-200 text-blue-600 pointer-events-auto opacity-90 hover:opacity-100"
            title="Deslizar para a direita"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        <div className={`hidden lg:flex absolute left-4 bottom-[6.5rem] top-0 w-16 z-10 items-end justify-start pointer-events-none transition-opacity duration-300 ${canScrollLeft ? 'opacity-100' : 'opacity-0'}`}>
          <Button
            variant="outline"
            size="icon"
            onClick={scrollToLeft}
            className="rounded-full w-8 h-8 bg-white shadow-md border-gray-200 text-blue-600 pointer-events-auto opacity-90 hover:opacity-100"
            title="Deslizar para a esquerda"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
        </div>

        <div className={`hidden lg:flex absolute right-4 bottom-[6.5rem] top-0 w-16 z-10 items-end justify-end pointer-events-none transition-opacity duration-300 ${canScrollRight ? 'opacity-100' : 'opacity-0'}`}>
          <Button
            variant="outline"
            size="icon"
            onClick={scrollToRight}
            className="rounded-full w-8 h-8 bg-white shadow-md border-gray-200 text-blue-600 pointer-events-auto opacity-90 hover:opacity-100"
            title="Deslizar para a direita"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        <div 
          className="overflow-x-auto custom-scrollbar pb-2" 
          ref={tableScrollRef}
          onScroll={checkForScrollPosition}
        >
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Protocolo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Atendente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nota
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prioridade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Espera (Fila)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duração
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Finalizado em
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="12" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
                      <p className="text-gray-500">Buscando atendimentos...</p>
                    </div>
                  </td>
                </tr>
              ) : tickets.length === 0 ? (
                <tr>
                  <td
                    colSpan="12"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    Nenhum atendimento encontrado
                  </td>
                </tr>
              ) : (
                tickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {ticket.protocol}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div
                        className="font-semibold truncate max-w-[150px] sm:max-w-[200px] md:max-w-[250px]"
                        title={ticket.client_name}
                      >
                        {ticket.client_name}
                      </div>

                      {ticket.external_client_company && (
                        <div
                          className="text-xs text-purple-600 font-medium truncate mt-0.5 max-w-[150px] sm:max-w-[200px] md:max-w-[250px]"
                          title={ticket.external_client_company}
                        >
                          🏢 {ticket.external_client_company}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {ticket.external_client_data?.real_phone ? (
                        <span className="text-green-600 font-medium">
                          {formatWhatsAppNumber(
                            ticket.external_client_data.real_phone,
                          )}
                        </span>
                      ) : ticket.client_phone.length > 14 ? (
                        `ID Oculto (${ticket.client_phone})`
                      ) : (
                        formatWhatsAppNumber(ticket.client_phone)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {ticket.assigned_to_name || "Não atribuído"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {ticket.category || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(ticket.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-yellow-600">
                      {ticket.rating !== null &&
                      ticket.rating !== undefined &&
                      ticket.rating >= 0 ? (
                        `⭐ ${ticket.rating}`
                      ) : (
                        <span className="text-gray-400 font-normal">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`${
                          ticket.priority === "low"
                            ? "text-green-600"
                            : ticket.priority === "medium"
                              ? "text-yellow-600"
                              : ticket.priority === "high"
                                ? "text-orange-600"
                                : "text-red-600"
                        }`}
                      >
                        {ticket.priority === "low"
                          ? "Baixa"
                          : ticket.priority === "medium"
                            ? "Média"
                            : ticket.priority === "high"
                              ? "Alta"
                              : "Urgente"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {ticket.wait_time_seconds !== null &&
                      ticket.wait_time_seconds !== undefined ? (
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              ticket.wait_time_seconds <= 300
                                ? "bg-green-500"
                                : ticket.wait_time_seconds <= 900
                                  ? "bg-yellow-500"
                                  : "bg-red-500"
                            }`}
                          ></span>
                          <span className="text-gray-700 font-medium">
                            {formatDuration(ticket.wait_time_seconds)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600 font-medium">
                      {ticket.duration_seconds !== null &&
                      ticket.duration_seconds !== undefined
                        ? formatDuration(ticket.duration_seconds)
                        : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {ticket.finished_at
                        ? formatDate(ticket.finished_at)
                        : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm flex gap-2">
                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700 bg-blue-50"
                      >
                        <Link
                          to={`/chat/${ticket.id}?view=attendance`}
                          title="Clique com o botão direito para abrir em uma nova guia"
                          data-testid={`history-view-ticket-${ticket.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Visualizar
                        </Link>
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCreateNewTicket(ticket)}
                        className="text-green-600 hover:text-green-700 bg-green-50"
                      >
                        <MessageSquarePlus className="w-4 h-4 mr-1" />
                        Novo Atendimento
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200 sm:px-6 rounded-b-lg">
          <div className="flex-1 flex justify-between sm:hidden">
            <Button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              variant="outline"
            >
              Anterior
            </Button>
            <Button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              variant="outline"
            >
              Próximo
            </Button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando página{" "}
                <span className="font-medium">{currentPage}</span> de{" "}
                <span className="font-medium">{totalPages}</span>
                <span className="ml-2 text-gray-400">
                  ({totalTickets} tickets no total)
                </span>
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                Primeira
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Próxima
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tickets;
